// The events a signed-in user attended or is registered for (#profile's Events tab), newest first,
// from Clerk publicMetadata.events: {"workshop-2026": {"in-person": [roles], "virtual": [roles],
// "online": [roles]}, ...}. An event counts when a delivery has a role (participant, speaker,
// organizer, session chair, sponsor), not just the "selected" mark of an unfinished registration.
// Dates come from that year's keyDates.json. receiptUrl: for a VIZBI Online event the user paid
// for (the online participant role, the year's record "paid", and no April role making it free),
// its paid invoice's Stripe page: the record's invoices[eventKey], saved per event by the payment
// webhook (one event bought later has its own invoice); before that, the record's hostedUrl.
// For the current year's events (April and online), paymentLabel when there's no link: "Paid"
// (bought, but no invoice link saved, e.g. paid before 31 March 2026) or "Free" (a crew role, the
// online event free through April, a 2-for-1 place, or no payment recorded).

import {computed} from "vue";
import {DateTime} from "luxon";
import history from "@/assets/history.json";
import useUserSafe from "@/composables/useUserSafe";
import {
  getIsShifted, getLocalDays, getLocalNote, getLongDates, getViewerZone
} from "@/composables/useLocalEventDates";

const ROLES = ["participant", "speaker", "organizer", "session-chair", "sponsor"];
const EVENT_TYPES = ["masterclass", "workshop", "showcase"];
// April's online delivery was the Virtual Workshop or Masterclass: "Virtual", its name (Seán,
// 2026-10-23); November's is VIZBI Online: "Online"
const DELIVERY_LABELS = {"in-person": "In person", virtual: "Virtual", online: "Online"};
const CREW_ROLES = ["speaker", "organizer", "session-chair", "sponsor"];
const APRIL_DELIVERIES = ["in-person", "virtual"];
const LABELLED_YEAR = 2026; // the years whose cards say Paid or Free

const keyDatesFiles = import.meta.webpackContext("../assets/years", {
  recursive: true,
  regExp: /keyDates\.json$/
});

function fetchKeyDates(year) {
  try {
    return keyDatesFiles(`./${year}/keyDates.json`);
  } catch {
    return {};
  }
}

// the keyDates.json entries that may name this event and delivery, e.g. "VIZBI Online Workshop"
function getKeyDateNames(title, delivery) {
  if (delivery === "online") return [`VIZBI Online ${title}`];
  if (delivery === "virtual") return [`VIZBI ${title} (online)`];
  return [`VIZBI ${title} (in-person)`, `VIZBI ${title} (in-person only)`, `VIZBI ${title}`];
}

// "November 18-19" → {start, end} in the year's time zone; null if not a day range
function getDateRange(dates, year) {
  const match = String(dates || "").match(/^(\w+)\s+(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  const [, month, startDay, endDay] = match;
  const zone = history.years[year];
  const parse = (day) => DateTime.fromFormat(`${month} ${day} ${year}`, "LLLL d yyyy", {zone});
  const start = parse(startDay);
  return start.isValid ? {start, end: parse(endDay || startDay).endOf("day")} : null;
}

function getDateText({start, end}) {
  const last = end.toFormat("d LLLL yyyy");
  return start.hasSame(end, "day") ? last : `${start.toFormat("d")}–${last}`;
}

// the year's one hostedUrl, unless it's an online event's own invoice (a later purchase replaced
// it)
function getAprilInvoiceUrl(record) {
  const hostedUrl = record?.hostedUrl || "";
  const onlineInvoices = Object.values(record?.invoices || {});
  return onlineInvoices.includes(hostedUrl) ? "" : hostedUrl;
}

// an April delivery: bought (a participant, the year's record paid), else free (crew, or comped)
function getAprilPayment({roles, record}) {
  const isBought = !roles.some((role) => CREW_ROLES.includes(role)) && record?.status === "paid";
  if (!isBought) return {receiptUrl: "", paymentLabel: "Free"};
  const receiptUrl = getAprilInvoiceUrl(record);
  return {receiptUrl, paymentLabel: receiptUrl ? "" : "Paid"};
}

function getOnlinePayment({deliveries, delivery, roles, record, eventKey}) {
  if (!getIsPaidOnline({deliveries, delivery, roles, record})) {
    return {receiptUrl: "", paymentLabel: "Free"};
  }
  const receiptUrl = record?.invoices?.[eventKey] || record?.hostedUrl || "";
  return {receiptUrl, paymentLabel: receiptUrl ? "" : "Paid"};
}

// {receiptUrl, paymentLabel}: the label only for LABELLED_YEAR; earlier years show neither
function getPayment(context) {
  const {delivery, year} = context;
  const isOnlinePaid = delivery === "online" && getIsPaidOnline(context);
  if (year !== LABELLED_YEAR) {
    const receiptUrl = isOnlinePaid ? getOnlinePayment(context).receiptUrl : "";
    return {receiptUrl, paymentLabel: ""};
  }
  if (APRIL_DELIVERIES.includes(delivery)) return getAprilPayment(context);
  return getOnlinePayment(context);
}

function getIsPaidOnline({deliveries, delivery, roles, record}) {
  if (delivery !== "online" || !roles.includes("participant") || record?.status !== "paid") {
    return false;
  }
  const getHasRole = (april) => (deliveries[april] || []).some((role) => ROLES.includes(role));
  return !["in-person", "virtual"].some(getHasRole); // attended in April: it was free
}

// an online session's dates on the viewer's calendar (useLocalEventDates), e.g. "19–20 November
// 2026" in Sydney; otherwise the dates as listed
function getDisplayDates(item, range, {year, zone}) {
  const localDays = getLocalDays(item, zone, year);
  const note = getIsShifted(item, zone, year) ? ` (${getLocalNote(zone)})` : "";
  if (localDays.length) return `${getLongDates(localDays)} ${year}${note}`;
  return range ? getDateText(range) : String(year);
}

function getEventItem(type, year, delivery, zone) {
  const title = type.charAt(0).toUpperCase() + type.slice(1);
  const keyDates = fetchKeyDates(year);
  const name = getKeyDateNames(title, delivery).find((key) => keyDates[key]);
  const range = getDateRange(keyDates[name]?.dates, year);
  return {
    key: `${type}-${year}-${delivery}`,
    type,
    title,
    deliveryLabel: DELIVERY_LABELS[delivery] || delivery,
    dateText: getDisplayDates(keyDates[name], range, {year, zone}),
    sortValue: range ? range.start.toMillis() : DateTime.fromObject({year}).toMillis(),
    endValue: range ? range.end.toMillis() : DateTime.fromObject({year}).endOf("year").toMillis()
  };
}

// "workshop-2026" → ["workshop", 2026]; null for other events (reception, gala dinner)
function getTypeAndYear(eventKey) {
  const match = String(eventKey).match(/^([a-z-]+)-(\d{4})$/);
  if (!match || !EVENT_TYPES.includes(match[1])) return null;
  return [match[1], Number(match[2])];
}

function getAttendedItems(events, unsafeMetadata, zone) {
  const items = [];
  for (const [eventKey, deliveries] of Object.entries(events || {})) {
    const typeAndYear = getTypeAndYear(eventKey);
    if (!typeAndYear) continue;
    const year = typeAndYear[1];
    const record = unsafeMetadata?.[`vizbi-${year}`];
    for (const [delivery, roles] of Object.entries(deliveries || {})) {
      if (!Array.isArray(roles) || !roles.some((role) => ROLES.includes(role))) continue;
      const payment = getPayment({deliveries, delivery, roles, record, eventKey, year});
      items.push({...getEventItem(...typeAndYear, delivery, zone), ...payment});
    }
  }
  return items.sort((first, second) => second.sortValue - first.sortValue);
}

// upcoming (not ended yet), soonest first; previous, newest first
export default function useAttendedEvents() {
  const {user} = useUserSafe();
  const attendedEvents = computed(() => {
    const {publicMetadata, unsafeMetadata} = user.value || {};
    return getAttendedItems(publicMetadata?.events, unsafeMetadata, getViewerZone(user.value));
  });
  const upcomingEvents = computed(() => {
    const now = Date.now();
    return attendedEvents.value.filter((item) => item.endValue >= now).reverse();
  });
  const previousEvents = computed(() => {
    return attendedEvents.value.filter((item) => item.endValue < Date.now());
  });
  return {attendedEvents, upcomingEvents, previousEvents};
}
